import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getAllSession } from '../utils/api/sessionAPI';
import { ChatSessionAnalysis } from './child/ChatSessionAnalysis';
import { MessageSquarePlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchAssistants } from '../store/app/assistantSlice';

const ITEMS_PER_PAGE = 100;

const ChatLogsLayer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const listOfAssistants = useSelector((s) => s?.assistants?.value);
  const assistant = useSelector((s) => s?.assistants);
  const userOrgId = useSelector((state) => state?.user?.user?.organizationId);

  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch assistants on component mount
  useEffect(() => {
    dispatch(fetchAssistants(userOrgId));
  }, [dispatch]);

  const [filterAssistantId, setFilterAssistantId] = useState(null);
  const [defaultAssistant, setDefaultAssistant] = useState(null);

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  // selected session for chat view
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  // init assistant filter from URL (and normalize)
  useEffect(() => {
    if (isInitialized) return;
    const assistantIdFromURL = searchParams.get('assistantId');

    if (!assistantIdFromURL || assistantIdFromURL === 'all') {
      setDefaultAssistant(null);
      setFilterAssistantId(null);
      if (assistantIdFromURL !== 'all') {
        const np = new URLSearchParams(searchParams);
        np.set('assistantId', 'all');
        setSearchParams(np);
      }
    } else {
      const found = listOfAssistants.find((a) => a?.id === assistantIdFromURL);
      if (found) {
        setDefaultAssistant(found);
        setFilterAssistantId(found.id);
      } else {
        // fallback to 'all' if not in list
        setDefaultAssistant(null);
        setFilterAssistantId(null);
        const np = new URLSearchParams(searchParams);
        np.set('assistantId', 'all');
        setSearchParams(np);
      }
    }
    setIsInitialized(true);
  }, [isInitialized, listOfAssistants, searchParams, setSearchParams]);

  // open chat view if sessionId exists in URL
  useEffect(() => {
    const sid = searchParams.get('sessionId');
    if (sid) {
      setSelectedSessionId(sid);
    }
  }, [searchParams]);

  // fetch sessions on filter/page change
  useEffect(() => {
    if (!isInitialized) return;
    let isCancelled = false;

    const run = async () => {
      setIsLoading(true);
      try {
        const res = await getAllSession({
          page,
          limit: ITEMS_PER_PAGE,
          assistantId: filterAssistantId || undefined,
        });
        if (isCancelled) return;
        setRows(Array.isArray(res?.sessions) ? res.sessions : []);
        setTotal(Number(res?.total || 0));
      } catch (e) {
        if (!isCancelled) {
          console.error('Failed to load sessions:', e?.message || e);
          setRows([]);
          setTotal(0);
        }
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    run();
    return () => {
      isCancelled = true;
    };
  }, [isInitialized, filterAssistantId, page]);

  const handleAssistantChange = (assistantId) => {
    if (assistantId === 'all') {
      setDefaultAssistant(null);
      setFilterAssistantId(null);
      setPage(1);
      setSelectedSessionId(null);
      const np = new URLSearchParams(searchParams);
      np.set('assistantId', 'all');
      np.delete('sessionId');
      setSearchParams(np);
      return;
    }
    const selected = listOfAssistants.find((a) => a.id === assistantId);
    if (!selected) return;
    setDefaultAssistant(selected);
    setFilterAssistantId(selected.id);
    setPage(1);
    setSelectedSessionId(null);
    const np = new URLSearchParams(searchParams);
    np.set('assistantId', selected.id);
    np.delete('sessionId');
    setSearchParams(np);
  };

  const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      style={{ height: '83vh', maxHeight: '83vh', overflow: 'hidden' }}
    >
      {/* Header */}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-2" style={{
        justifyContent: 'space-between',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1, maxWidth: '300px' }}>
          <label htmlFor="assistant-select" style={{ whiteSpace: 'nowrap' }}>Assistant:</label>
          <div className="position-relative" style={{ flex: 1 }}>
            <Icon
              icon="lucide:bot"
              width="18"
              height="18"
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: '#6b7280',
                zIndex: 1
              }}
            />
            <select
              id="assistant-select"
              name="assistant_id"
              className="form-control"
              value={defaultAssistant?.id || 'all'}
              onChange={(e) => handleAssistantChange(e.target.value)}
              required
              style={{
                paddingLeft: '36px',
                paddingRight: '30px',
                height: '45px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                backgroundColor: '#fff',
                width: '100%',
              }}
            >
              <option value="all">All Assistants</option>
              {defaultAssistant?.id &&
                !listOfAssistants.some((a) => a.id === defaultAssistant.id) && (
                  <option value={defaultAssistant.id}>{defaultAssistant.name}</option>
                )}
              {listOfAssistants.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <Icon
              icon="lucide:chevron-down"
              width="18"
              height="18"
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: '#6b7280',
              }}
            />
          </div>
        </div>
        <div className="d-flex align-items-center text-sm" style={{ gap: '8px', cursor: 'pointer', color: 'var(--primary-500)' }} onClick={() => window.open('https://docs.callhq.ai/api-reference/chat/create-session', '_blank')}>
          <MessageSquarePlus size={20} />
          <span>Create your own chat</span>
        </div>
      </div>

      {/* WhatsApp-like Split View */}
      <div className="chat-wrapper" style={{ 
        display: 'flex', 
        height: 'calc(83vh - 73px)',
        maxHeight: 'calc(83vh - 73px)',
        overflow: 'hidden'
      }}>
        {/* Left Panel - Chat List (33.33% width) */}
        <div className="chat-sidebar card" style={{
          width: '33.333%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 0,
          borderRight: '1px solid #e5e7eb',
          height: '100%'
        }}>
          {/* Chat List */}
          <div className="chat-all-list" style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            minHeight: 0,
            maxHeight: '100%'
          }}>
            {isLoading ? (
              <div className="p-3">
                {Array.from({ length: 5 }).map((_, idx) => (
                  <div key={`shimmer-${idx}`} className="chat-sidebar-single d-flex align-items-center gap-3 p-3" style={{ borderBottom: '1px solid #f0f2f5' }}>
                    <div className="shimmer shimmer-avatar" style={{ width: '50px', height: '50px', borderRadius: '50%' }} />
                    <div style={{ flex: 1 }}>
                      <div className="shimmer shimmer-text mb-2" style={{ width: '60%', height: '14px' }} />
                      <div className="shimmer shimmer-text" style={{ width: '80%', height: '12px' }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : rows.length > 0 ? (
              rows.map((s) => (
                <div
                  key={s.id}
                  onClick={() => {
                    setSelectedSessionId(s.id);
                    const np = new URLSearchParams(searchParams);
                    np.set('sessionId', s.id);
                    setSearchParams(np);
                  }}
                  className={`chat-sidebar-single ${selectedSessionId === s.id ? 'active' : ''}`}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedSessionId === s.id ? '#f0f2f5' : '#fff'
                  }}
                >
                  <div className="img">
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      backgroundColor: '#25D366',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      fontWeight: '600'
                    }}>
                      {s?.assistant?.name?.[0]?.toUpperCase() || s?.name?.[0]?.toUpperCase() || 'C'}
                    </div>
                  </div>
                  <div className="info">
                    <h6 className="text-sm mb-1">{s?.name || s?.assistant?.name || 'Unnamed Session'}</h6>
                    <p className="mb-0 text-xs">{s?.assistant?.name || 'No assistant'}</p>
                  </div>
                  <div className="action text-end">
                    <p className="mb-0 text-neutral-400 text-xs lh-1">
                      {s?.status ? s.status[0].toUpperCase() + s.status.slice(1) : 'Active'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="d-flex flex-column justify-content-center align-items-center" style={{ 
                height: '100%', 
                padding: '40px',
                textAlign: 'center'
              }}>
                <Icon icon="icon-park-outline:file-search" style={{ fontSize: '48px', color: '#667781', marginBottom: '12px' }} />
                <p className="text-secondary-light mb-0" style={{ fontSize: '14px' }}>No sessions found</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {rows.length > 0 && (
            <div className="d-flex align-items-center justify-content-center gap-2 p-3" style={{
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#f0f2f5',
              flexShrink: 0,
              marginTop: 'auto'
            }}>
              <button
                className="btn btn-sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={!canPrev}
                style={{
                  backgroundColor: !canPrev ? '#f0f2f5' : '#fff',
                  border: '1px solid #e5e7eb',
                  color: !canPrev ? '#aebac1' : '#111b21',
                  cursor: !canPrev ? 'not-allowed' : 'pointer'
                }}
              >
                Prev
              </button>
              <span style={{ fontSize: '13px', color: '#667781' }}>
                Page {page} of {totalPages}
              </span>
              <button
                className="btn btn-sm"
                onClick={() => setPage((p) => (canNext ? p + 1 : p))}
                disabled={!canNext}
                style={{
                  backgroundColor: !canNext ? '#f0f2f5' : '#fff',
                  border: '1px solid #e5e7eb',
                  color: !canNext ? '#aebac1' : '#111b21',
                  cursor: !canNext ? 'not-allowed' : 'pointer'
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Right Panel - Chat Messages (66.67% width) */}
        <div className="chat-main card" style={{
          width: '66.667%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 0,
          height: '100%',
        }}>
          {selectedSessionId ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              height: '100%',
              width: '100%',
              overflow: 'hidden',
              minHeight: 0,
              flex: 1
            }}>
              <ChatSessionAnalysis
                sessionId={selectedSessionId}
                setAnalysisModal={null}
              />
            </div>
          ) : (
            <div className="d-flex flex-column justify-content-center align-items-center" style={{ 
              height: '100%', 
              padding: '40px',
              textAlign: 'center'
            }}>
              <Icon icon="mdi:chat-outline" style={{ fontSize: '64px', color: '#667781', marginBottom: '16px', opacity: 0.5 }} />
              <h6 style={{ color: '#667781', fontSize: '16px', marginBottom: '8px' }}>Select a chat to view messages</h6>
              <p style={{ color: '#667781', fontSize: '14px', opacity: 0.7 }}>Choose a session from the list to start viewing the conversation</p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ChatLogsLayer;
